import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';

const ForgotUserId = () => {
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [countryIsdCode, setCountryIsdCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [recoveredUserId, setRecoveredUserId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    forgotUserIdByEmail,
    forgotUserIdSendPhoneOtp,
    forgotUserIdVerifyPhoneOtp,
  } = useAuth();

  const phoneRule = getPhoneRuleByIsd(countryIsdCode);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setRecoveredUserId('');
    setLoading(true);

    if (mode === 'email') {
      const result = await forgotUserIdByEmail(email.trim());
      if (result.success) {
        setSuccess(result.message || 'User ID has been sent to your email.');
      } else {
        setError(result.message || 'Unable to process request.');
      }
      setLoading(false);
      return;
    }

    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < phoneRule.minLength || cleanPhone.length > phoneRule.maxLength) {
      setError(`Enter a valid mobile number for ${countryIsdCode}`);
      setLoading(false);
      return;
    }

    if (!otpSent) {
      const result = await forgotUserIdSendPhoneOtp(countryIsdCode, cleanPhone);
      if (result.success) {
        setOtpSent(true);
        setSuccess('OTP sent to your WhatsApp number. Enter OTP to view your User ID.');
      } else {
        setError(result.message || 'Unable to send OTP.');
      }
      setLoading(false);
      return;
    }

    const verifyResult = await forgotUserIdVerifyPhoneOtp(countryIsdCode, cleanPhone, otp.trim());
    if (verifyResult.success) {
      setRecoveredUserId(verifyResult.userId || '');
      setSuccess('User ID retrieved successfully.');
    } else {
      setError(verifyResult.message || 'Invalid OTP');
    }

    setLoading(false);
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h3>Forgot User ID</h3>
            </Card.Header>
            <Card.Body>
              <Nav
                variant="tabs"
                activeKey={mode}
                className="mb-3"
                onSelect={(selected) => {
                  setMode(selected || 'email');
                  setError('');
                  setSuccess('');
                  setRecoveredUserId('');
                  setOtpSent(false);
                  setOtp('');
                }}
              >
                <Nav.Item>
                  <Nav.Link eventKey="email">Recover via Email</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="phone">Recover via Phone OTP</Nav.Link>
                </Nav.Item>
              </Nav>

              <Form onSubmit={handleSubmit}>
                {mode === 'email' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Registered Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your registered email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Form.Group>
                )}

                {mode === 'phone' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Country ISD Code</Form.Label>
                      <Form.Select
                        value={countryIsdCode}
                        onChange={(e) => setCountryIsdCode(e.target.value)}
                        required
                      >
                        {COUNTRY_ISD_CODES.map((entry) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Enter your registered mobile number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, phoneRule.maxLength))}
                        required
                      />
                    </Form.Group>

                    {otpSent && (
                      <Form.Group className="mb-3">
                        <Form.Label>OTP</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                        />
                      </Form.Group>
                    )}
                  </>
                )}

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {recoveredUserId && (
                  <Alert variant="info">
                    Your User ID is: <strong>{recoveredUserId}</strong>
                  </Alert>
                )}

                <div className="d-grid">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading
                      ? 'Please wait...'
                      : mode === 'email'
                        ? 'Send User ID'
                        : otpSent
                          ? 'Verify OTP'
                          : 'Send OTP'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
            <Card.Footer className="text-center">
              <Link to="/login" className="text-decoration-none">Back to Login</Link>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotUserId;
